import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { exportOrder } from "@/lib/exportOrder";
import { useTableSort } from "@/lib/tableSort";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, Cell,
} from "recharts";
import { Download, FileText, Search, Lightbulb, ShieldCheck } from "lucide-react";

const NAVY = "#0F2C59";
const GREEN = "#16A34A";
const AMBER = "#D97706";
const RED = "#DC2626";

const Stat = ({ label, value, sub, color }) => (
  <div className="rounded-xl border border-border p-3" data-testid={`cdr-stat-${label}`}>
    <p className="text-2xl font-bold tabular-nums" style={{ color }}>{value}</p>
    <p className="text-xs font-medium">{label}</p>
    {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
  </div>
);

export const CommitteeDocReport = ({ committeeNo = "all" }) => {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [view, setView] = useState("all");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setData(null);
    const params = committeeNo !== "all" ? { committee_no: Number(committeeNo) } : {};
    api.get("/reports/committee-document-report", { params })
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Komite belge raporu yüklenemedi"));
  }, [committeeNo]);

  const rows = (data?.rows || []).filter((r) => {
    if (view === "belge_yok" && r.proxy_document) return false;
    if (view === "konum_bos" && !(r.proxy_document && !r.document_holder)) return false;
    if (view === "yetkili_bos" && r.voting_person) return false;
    if (view === "hazir" && !r.ready) return false;
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [r.company_name, r.sicil_no, r.document_holder, r.voting_person, r.committee_name]
      .some((x) => String(x || "").toLowerCase().includes(s));
  });
  const t = useTableSort(rows);

  const download = async (format) => {
    setBusy(true);
    try {
      const order = await exportOrder(t.sorted.map((r) => r.id));
      const res = await api.get("/reports/committee-document-report/export", {
        params: { format, only: 1, order, ...(committeeNo !== "all" ? { committee_no: Number(committeeNo) } : {}) },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const base = committeeNo !== "all"
        ? `${Number(committeeNo)}. KOMİTE YETKİ BELGESİ RAPORU`
        : "TÜM KOMİTELER YETKİ BELGESİ RAPORU";
      a.download = `${base}.${format === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch {
      toast.error("Dışa aktarma başarısız");
    } finally { setBusy(false); }
  };

  if (!data) return <Skeleton className="h-64 w-full" />;
  const s = data.summary;
  const chart = (data.committees || []).slice(0, 12).map((c) => ({
    name: `#${c.committee_no}`, full: c.name, Belge: c.belge, Hedef: c.hedef, pct: c.pct,
  }));

  return (
    <div className="space-y-4" data-testid="cdr-report">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat label="KESİN firma" value={s.total} sub="FAAL · tüzel" color={NAVY} />
        <Stat label="Belgesi alınmış" value={s.belge} sub={`hedef ${s.hedef} · %${s.pct}`} color={GREEN} />
        <Stat label="Belgesi yok" value={s.belge_yok} sub="toplanacak" color={RED} />
        <Stat label="Belge yeri girilmemiş" value={s.konum_bos} sub="seçim günü riski" color={AMBER} />
        <Stat label="Yetkilisi girilmemiş" value={s.yetkili_bos} sub={`${s.hazir} firma tam hazır`} color={AMBER} />
      </div>

      <Card className="p-4">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <p className="text-sm font-medium">Hedefe göre durum</p>
          <span className="text-xs text-muted-foreground">
            Belge {s.belge} / {s.hedef} · <b style={{ color: s.pct >= 100 ? GREEN : AMBER }}>%{s.pct}</b>
          </span>
        </div>
        <div className="h-3 w-full rounded-full bg-muted overflow-hidden mb-4">
          <div className="h-full rounded-full transition-all" data-testid="cdr-progress"
            style={{ width: `${Math.min(s.pct, 100)}%`, background: s.pct >= 100 ? GREEN : NAVY }} />
        </div>
        <div style={{ width: "100%", height: 250 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <BarChart data={chart} margin={{ top: 10, right: 8, left: -22, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" fontSize={11} />
              <YAxis allowDecimals={false} fontSize={10} />
              <Tooltip formatter={(v, n) => [v, n]} labelFormatter={(l) => chart.find((c) => c.name === l)?.full || l} />
              <Legend />
              <Bar dataKey="Hedef" fill="#CBD5E1" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Belge" radius={[4, 4, 0, 0]}>
                {chart.map((c, i) => <Cell key={i} fill={c.pct >= 100 ? GREEN : c.pct >= 60 ? NAVY : RED} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <p className="text-[11px] text-muted-foreground mt-1">
          Hedefi en zayıf 12 komite gösterilir. Gri sütun hedef (KESİN oyun %60'ı), renkli sütun alınan belge.
        </p>
      </Card>

      <Card className="p-4 border-l-4" style={{ borderLeftColor: AMBER }} data-testid="cdr-advice">
        <div className="flex items-start gap-3">
          <Lightbulb className="h-5 w-5 shrink-0 mt-0.5" style={{ color: AMBER }} />
          <div className="space-y-1">
            <p className="text-sm font-semibold">Tavsiye</p>
            {(data.advice || []).map((a, i) => (
              <p key={i} className="text-sm text-muted-foreground" data-testid={`cdr-advice-${i}`}>{a}</p>
            ))}
          </div>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex flex-wrap items-center gap-2">
          <ShieldCheck className="h-4 w-4" style={{ color: NAVY }} />
          <p className="text-sm font-medium">KESİN firmalar · belge durumu</p>
          <span className="text-xs text-muted-foreground">{t.sorted.length} kayıt</span>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <Select value={view} onValueChange={setView}>
              <SelectTrigger className="h-9 w-[210px]" data-testid="cdr-view"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="belge_yok">Belgesi alınmamış</SelectItem>
                <SelectItem value="konum_bos">Belge yeri girilmemiş</SelectItem>
                <SelectItem value="yetkili_bos">Yetkilisi girilmemiş</SelectItem>
                <SelectItem value="hazir">Tam hazır</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Firma, yetkili, sicil ara..."
                className="h-9 w-52 pl-8" data-testid="cdr-search" />
            </div>
            <Button variant="outline" size="sm" className="h-9" disabled={busy} onClick={() => download("xlsx")} data-testid="cdr-export-xlsx">
              <Download className="h-4 w-4 mr-1.5" />Excel
            </Button>
            <Button variant="outline" size="sm" className="h-9" disabled={busy} onClick={() => download("pdf")} data-testid="cdr-export-pdf">
              <FileText className="h-4 w-4 mr-1.5" />PDF
            </Button>
          </div>
        </div>
        {t.sorted.length === 0 ? (
          <p className="p-10 text-center text-sm text-muted-foreground" data-testid="cdr-empty">Kayıt bulunamadı.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cdr-table">
              <thead>
                <tr className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
                  <t.Th sortKey="cdr-firma" get={(r) => r.company_name} text className="px-3 py-3 font-medium">Firma</t.Th>
                  <t.Th sortKey="cdr-komite" get={(r) => r.committee_no} className="px-3 py-3 font-medium">Komite</t.Th>
                  <t.Th sortKey="cdr-belge" get={(r) => (r.proxy_document ? 1 : 0)} className="px-3 py-3 font-medium">Yetki Belgesi</t.Th>
                  <t.Th sortKey="cdr-konum" get={(r) => r.document_holder} text className="px-3 py-3 font-medium">Belge Kimde / Nerede</t.Th>
                  <t.Th sortKey="cdr-yetkili" get={(r) => r.voting_person} text className="px-3 py-3 font-medium">Oy Kullanacak Yetkili</t.Th>
                  <t.Th sortKey="cdr-hazir" get={(r) => (r.ready ? 1 : 0)} className="px-3 py-3 font-medium">Durum</t.Th>
                </tr>
              </thead>
              <tbody>
                {t.sorted.map((r) => (
                  <tr key={r.id} className="border-t border-border/60 hover:bg-muted/40" data-testid={`cdr-row-${r.id}`}>
                    <td className="px-3 py-2 font-medium">
                      {r.company_name}
                      {r.sicil_no && <span className="text-xs text-muted-foreground"> · {r.sicil_no}</span>}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">#{r.committee_no} · {r.branch || "—"}</td>
                    <td className="px-3 py-2">
                      {r.proxy_document
                        ? <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">Alındı</span>
                        : <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300">Yok</span>}
                    </td>
                    <td className="px-3 py-2">{r.document_holder || <span className="text-amber-600 text-xs">girilmemiş</span>}</td>
                    <td className="px-3 py-2">{r.voting_person || <span className="text-amber-600 text-xs">girilmemiş</span>}</td>
                    <td className="px-3 py-2">
                      {r.ready
                        ? <span className="text-xs font-semibold" style={{ color: GREEN }}>Tam hazır</span>
                        : <span className="text-xs text-muted-foreground">Eksik var</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default CommitteeDocReport;
